import React, { FC, useState } from 'react'
import { toast } from 'react-toastify'
import { KTSVG } from '../../../../_metronic/helpers'
import {
  searchAuxiliaryEngineReports,
  searchBoilerPerformanceReports,
  searchCompressorSystemReports,
  searchPurifierReports,
  searchInertGasSystemReports,
  searchBwtsReports,
  searchFreshWaterGeneratorReports,
} from '../core/_requests'
import { generateMachineryStatusPdf } from './MachineryStatusPdfGenerator'
import type { Vessel, Company, CompanyAdmin } from '../../Management/core/_models'
import QhseAccessDenied from './QhseAccessDenied'
import SearchableSelect from '../../../shared/components/SearchableSelect'

type Props = {
  isOpen: boolean
  onClose: () => void
  canFilterCompanies: boolean
  canRead?: boolean
  vessels: Vessel[]
  companyAdmins: CompanyAdmin[]
  subCompanies: Company[]
  /** Pre-selected vessel id for non-superadmin users */
  defaultVesselId?: number | null
}

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const MONTHS_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

const currentYear = new Date().getFullYear()
const currentMonth = new Date().getMonth() + 1

const MachineryStatusPdfModal: FC<Props> = ({
  isOpen,
  onClose,
  canFilterCompanies,
  canRead = true,
  vessels,
  companyAdmins,
  subCompanies,
  defaultVesselId,
}) => {
  const [filterCompanyGroup, setFilterCompanyGroup] = useState('')
  const [filterCompanyAdmin, setFilterCompanyAdmin] = useState('')
  const [filterVesselId, setFilterVesselId] = useState<number>(defaultVesselId || 0)
  const [filterMonth, setFilterMonth] = useState<number>(currentMonth)
  const [filterYear, setFilterYear] = useState<number>(currentYear)
  const [isGenerating, setIsGenerating] = useState(false)

  const filteredSubCompanies = subCompanies.filter(
    (c) => !filterCompanyGroup || String((c as any).companyGroupAdminId || (c as any).companyGroupAdmin?.id) === filterCompanyGroup
  )

  const filteredVessels = vessels.filter((v) => {
    if (filterCompanyGroup && String((v as any).companyGroupAdmin?.id) !== filterCompanyGroup) return false
    if (filterCompanyAdmin && String((v as any).companyAdmin?.id) !== filterCompanyAdmin) return false
    return true
  })

  const handleGenerate = async () => {
    const vesselId = canFilterCompanies ? filterVesselId : (defaultVesselId || filterVesselId)
    if (!vesselId) {
      toast.error('Please select a vessel', { position: 'top-center' })
      return
    }
    if (!filterMonth || !filterYear) {
      toast.error('Please select a month and year', { position: 'top-center' })
      return
    }

    setIsGenerating(true)
    try {
      const params = { vesselId, month: filterMonth, year: filterYear }

      const [ae, bp, cs, pu, ig, bw, fw] = await Promise.all([
        searchAuxiliaryEngineReports(params).catch(() => []),
        searchBoilerPerformanceReports(params).catch(() => []),
        searchCompressorSystemReports(params).catch(() => []),
        searchPurifierReports(params).catch(() => []),
        searchInertGasSystemReports(params).catch(() => []),
        searchBwtsReports(params).catch(() => []),
        searchFreshWaterGeneratorReports(params).catch(() => []),
      ])

      const vessel = vessels.find((v) => Number(v.id) === Number(vesselId))
      const vesselName = vessel?.fleet_name || vessel?.name || `Vessel ${vesselId}`
      const period = `${MONTHS_FULL[filterMonth - 1]} ${filterYear}`

      generateMachineryStatusPdf({
        vesselName,
        period,
        auxiliaryEngine: ae[0] ?? null,
        boilerPerformance: bp[0] ?? null,
        compressorSystem: cs[0] ?? null,
        purifier: pu[0] ?? null,
        inertGasSystem: ig[0] ?? null,
        bwts: bw[0] ?? null,
        freshWaterGenerator: fw[0] ?? null,
      })

      toast.success('PDF downloaded successfully', { position: 'top-center' })
      onClose()
    } catch (err) {
      console.error('PDF generation failed:', err)
      toast.error('Failed to generate PDF', { position: 'top-center' })
    } finally {
      setIsGenerating(false)
    }
  }

  if (!isOpen) return null
  if (!canRead) {
    return (
      <div
        className='modal fade show d-block'
        tabIndex={-1}
        role='dialog'
        aria-modal='true'
        style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
      >
        <div className='modal-dialog modal-md' role='document'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h5 className='modal-title fw-bold mb-0'>Download Machinery Status Report</h5>
              <button type='button' className='btn-close' onClick={onClose} />
            </div>
            <div className='modal-body'>
              <QhseAccessDenied message='Access denied — you do not have permission to download Machinery Status reports.' />
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className='modal fade show d-block'
      tabIndex={-1}
      role='dialog'
      aria-modal='true'
      style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
      onClick={onClose}
    >
      <div
        className='modal-dialog modal-md'
        role='document'
        onClick={(e) => e.stopPropagation()}
      >
        <div className='modal-content'>
          {/* Header */}
          <div className='modal-header'>
            <div>
              <h5 className='modal-title fw-bold mb-0'>Download Machinery Status Report</h5>
              <span className='text-muted fs-8'>All 7 sub-reports combined into one PDF</span>
            </div>
            <button type='button' className='btn-close' onClick={onClose} />
          </div>

          {/* Body */}
          <div className='modal-body'>
            <div className='row g-3'>

              {/* SuperAdmin-only filters */}
              {canFilterCompanies && (
                <>
                  <div className='col-12'>
                    <label className='form-label fw-semibold'>Company</label>
                    <SearchableSelect
                      className='form-select form-select-sm'
                      value={filterCompanyGroup}
                      onChange={(e) => {
                        setFilterCompanyGroup(e.target.value)
                        setFilterCompanyAdmin('')
                        setFilterVesselId(0)
                      }}
                    >
                      <option value=''>All Companies</option>
                      {companyAdmins.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </SearchableSelect>
                  </div>

                  {filteredSubCompanies.length > 0 && (
                    <div className='col-12'>
                      <label className='form-label fw-semibold'>Sub-Company</label>
                      <SearchableSelect
                        className='form-select form-select-sm'
                        value={filterCompanyAdmin}
                        onChange={(e) => {
                          setFilterCompanyAdmin(e.target.value)
                          setFilterVesselId(0)
                        }}
                      >
                        <option value=''>All Sub-Companies</option>
                        {filteredSubCompanies.map((c) => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </SearchableSelect>
                    </div>
                  )}

                  <div className='col-12'>
                    <label className='form-label fw-semibold'>
                      Vessel <span className='text-danger'>*</span>
                    </label>
                    <SearchableSelect
                      className='form-select form-select-sm'
                      value={filterVesselId || ''}
                      onChange={(e) => setFilterVesselId(Number(e.target.value))}
                    >
                      <option value=''>Select Vessel</option>
                      {filteredVessels.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.fleet_name || v.name}
                        </option>
                      ))}
                    </SearchableSelect>
                  </div>
                </>
              )}

              {/* Month filter — all roles */}
              <div className='col-7'>
                <label className='form-label fw-semibold'>
                  Month <span className='text-danger'>*</span>
                </label>
                <SearchableSelect
                  className='form-select form-select-sm'
                  value={filterMonth}
                  onChange={(e) => setFilterMonth(Number(e.target.value))}
                >
                  {MONTHS_SHORT.map((m, i) => (
                    <option key={i + 1} value={i + 1}>{m}</option>
                  ))}
                </SearchableSelect>
              </div>

              <div className='col-5'>
                <label className='form-label fw-semibold'>Year <span className='text-danger'>*</span></label>
                <input
                  type='number'
                  className='form-control form-control-sm'
                  value={filterYear}
                  min={2000}
                  max={currentYear + 2}
                  onChange={(e) => setFilterYear(Number(e.target.value))}
                />
              </div>


            </div>
          </div>

          {/* Footer */}
          <div className='modal-footer'>
            <button type='button' className='btn btn-light btn-sm' onClick={onClose}>
              Cancel
            </button>
            <button
              type='button'
              className='btn btn-success btn-sm d-flex align-items-center gap-2'
              onClick={handleGenerate}
              disabled={isGenerating || (canFilterCompanies && !filterVesselId)}
            >
              {isGenerating ? (
                <>
                  <span className='spinner-border spinner-border-sm' />
                  Generating...
                </>
              ) : (
                <>
                  <KTSVG path='/media/icons/duotune/general/download.svg' className='svg-icon-4' />
                  Download PDF
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MachineryStatusPdfModal
